import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAbility } from '@casl/react';
import { CircularProgress } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Box } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';

import usePearsQuery from '@components/common/hooks/api/usePearsQuery';
import useCurriculaQuery from '@components/common/hooks/api/useCurriculaQuery';
import useStaffUsersQuery from '@components/common/hooks/api/useStaffUsersQuery';
import { axiosRequest } from '@lib/axios';
import { AbilityContext, Subjects } from '@lib/permissions';
import { scrollToAlert } from '@lib/utils';
import { PageContext } from '@lib/contexts/pageContext';
import UserContext from '@lib/contexts/userContext';
import EventForm from '../EventForm';

const CreateEvent = () => {
  const {
    setPageMessage,
    setPageErrorMessage,
  } = useContext(PageContext);
  const { userState: { userRecord } } = useContext(UserContext);

  const ability = useAbility(AbilityContext);

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [submitting, setSubmitting] = useState(false);
  const valuesSetState = useState(null);
  // const [, setValuesSet] = valuesSetState;

  const form = useForm({
    mode: 'onSubmit',
    defaultValues: {
      point: null,
      event_type: null,
      additional: null,
      curriculum: null,
      participant_needs: null,
      program_areas: null,
      created_by: userRecord?.email,
      event_name: null,
      event_partner: null,
      main_office: null,
      special_project: null,
      teen_parents: null,
      is_test: null,
      description: 'Event description coming in a future phase.',
    }
  });

  const staffUsersQuery = useStaffUsersQuery();
  const pearsQuery = usePearsQuery();
  const curriculaQuery = useCurriculaQuery();

  const isLoading = staffUsersQuery.isLoading || pearsQuery.isLoading || curriculaQuery.isLoading;

  /**
   * Submit handler.
   *
   * @param values
   */
  const onSubmit = async (values) => {
    setPageMessage(null);
    setPageErrorMessage(null);
    setSubmitting(true);

    const input = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      switch (key) {
        case 'additional':
        case 'curriculum':
          if (value?.id) {
            acc[`${key}_id`] = value?.id;
          }
          break;
        // case 'description':
        //   acc[key] = value || 'Event description coming in a future phase.';
        //   break;
        case 'event_name':
          acc[key] = value.trim();
          break;
        case 'participant_needs':
        case 'program_areas':
        case 'created_by':
        case 'event_partner':
        case 'event_type':
        case 'main_office':
        case 'points':
        case 'special_project':
          acc[key] = value?.id || value;
          break;
        case 'participant_needs_other':
          if (values.participant_needs.includes('other')) {
            acc[key] = value?.id || value;
          } else {
            acc[key] = null;
          }
          break;
        case 'teen_parents':
        case 'is_test':
          acc[key] = values[key] === 'true';
          break;
        default: break;
      }
      return acc;
    }, {});

    if (ability.cannot('create', Subjects.EVENT)) {
      setSubmitting(false);
      setPageErrorMessage('You do not have permission to create events.');
      scrollToAlert();
      return false;
    }
    // Staff users can only assign events to themselves
    if (input.created_by !== userRecord?.email && ability.cannot('assign', Subjects.EVENT)) {
      setSubmitting(false);
      setPageErrorMessage('You do not have permission to assign events to other staff members.');
      scrollToAlert();
      return false;
    }
    try {
      const result = await axiosRequest({
        method: 'POST',
        url: 'events',
        data: input,
      });
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      const id = result?.id;
      setSubmitting(false);
      navigate(`/events/${id}`, { state: { message: 'Event created' }});
    } catch (error) {
      console.log('Error creating event: ', error);
      setSubmitting(false);
      setPageErrorMessage('Error creating event. Please try again.', error);
      scrollToAlert();
    }
  };

  return (
    <Grid container gap={3} wrap={'nowrap'} columns={10}>
      <Grid xs={10}>
        { isLoading && (
          <Box width="100%" textAlign="center">
            <CircularProgress />
          </Box>
        )}
        { !isLoading && (
          <EventForm
            form={form}
            event={false}
            pearsData={pearsQuery.data}
            staffUsers={staffUsersQuery.data}
            availableCurriculum={curriculaQuery.data}
            submitting={submitting}
            onSubmit={onSubmit}
            valuesSetState={valuesSetState}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default CreateEvent;
